import { useEffect } from 'react';
import { installAudioUnlock } from '../audio/context';
import { installDeviceWatch } from '../audio/devices';
import { installInput } from '../input/installInput';
import { midi } from '../midi/MidiBus';
import { installBindings } from '../store/bindings';
import { useAppStore } from '../store/appStore';
import { preload } from '../core/preload';

/**
 * Wires everything that lives outside React: the audio graph, the MIDI bus,
 * the computer keyboard and the store bindings, and starts the preload the
 * loader reports on. Runs once for the life of the app.
 */
export function useBootstrap(): void {
  const setMidi = useAppStore((state) => state.setMidi);

  useEffect(() => {
    const teardown = [
      installBindings(),
      installInput(),
      installAudioUnlock(),
      installDeviceWatch(),
    ];

    // Fetches the models, the cubemap and every sample, and loads the default
    // one. What it can't get leaves that one slot silent rather than holding
    // the loader up: the instrument is still playable without it.
    void preload();

    let disposed = false;
    const inputs = () => midi.listPorts().inputs;
    const offPorts = midi.onPortsChanged(() => setMidi(midi.status, inputs()));

    void midi.init().then((status) => {
      if (!disposed) setMidi(status, inputs());
    });

    return () => {
      disposed = true;
      offPorts();
      for (const dispose of teardown) dispose();
    };
  }, [setMidi]);
}
